import { createClient, SupabaseClient, type Session } from '@supabase/supabase-js';
import { isPublicPath } from './publicRoutes';
import { notifySessionExpired } from './sessionExpiry';
import {
  ldosAuthClientOptions,
  migrateLegacyAuthSession,
  readPersistedAuthRecord,
} from './ldosAuthConfig';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://tsnbktwryvnbmgsvzxdt.supabase.co';
// SECURITY: client MUST use the anon (publishable) key ONLY. Never the service_role key.
// No hardcoded fallback — a missing/invalid key fails closed rather than shipping a secret.
const rawAnonKey: string | undefined = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Normalize the env var - treat 'undefined', 'null', empty strings as missing
const supabaseAnonKey = (
  rawAnonKey && 
  rawAnonKey !== 'undefined' && 
  rawAnonKey !== 'null' && 
  rawAnonKey.trim() !== ''
) ? rawAnonKey : null;

// Create a safe initialization that doesn't crash the app
let supabaseInstance: SupabaseClient | null = null;

if (supabaseUrl && supabaseAnonKey) {
  try {
    // R-029: carry any session supabase-js already stored under its own default
    // key (sb-<ref>-auth-token) over to the shared LDOS key BEFORE the client
    // reads storage — otherwise the key change would sign everyone out once.
    migrateLegacyAuthSession(supabaseUrl);
    // R-029: auth options are declared explicitly (never implied) and the
    // session lives under one ecosystem-wide storageKey, in a parent-domain
    // cookie when VITE_AUTH_COOKIE_DOMAIN is set. See src/lib/ldosAuthConfig.ts.
    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, ldosAuthClientOptions());
    console.log('Supabase client initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Supabase client:', error);
  }
} else {
  console.warn('Supabase not configured - VITE_SUPABASE_ANON_KEY is missing or invalid');
}

// Export a proxy that handles missing client gracefully
export const supabase = supabaseInstance as SupabaseClient;
export const isSupabaseConfigured = !!supabaseInstance;

/** True when the auth error means the refresh token is invalid/revoked/not found - session should be cleared */
export function isInvalidRefreshTokenError(error: unknown): boolean {
  const msg =
    error && typeof error === "object" && "message" in error
      ? String((error as { message: unknown }).message)
      : "";
  return (
    msg.includes("Refresh Token Not Found") ||
    msg.includes("Invalid Refresh Token") ||
    msg.includes("refresh_token_not_found") ||
    msg.includes("invalid_refresh_token")
  );
}

/** Clear stored session (removes bad refresh token from storage). Safe to call when Supabase not configured. */
export async function clearSessionOnInvalidToken(): Promise<void> {
  if (!isSupabaseConfigured) return;
  try {
    await supabase.auth.signOut({ scope: "local" });
  } catch {
    // Ignore signOut errors (e.g. already cleared)
  }
}

/** Clear session and, when possible, hand off to the in-app "session expired" prompt instead of
 * navigating away (see src/lib/sessionExpiry.ts). AuthProvider registers that handler once
 * mounted; if nothing is registered yet (e.g. very first load) this falls back to the old hard
 * redirect with returnTo preserved so re-login lands the user back where they were. */
export async function redirectToLoginOnSessionExpired(): Promise<void> {
  await clearSessionOnInvalidToken();
  if (notifySessionExpired()) return;
  if (typeof window !== "undefined" && !isPublicPath(window.location.pathname)) {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.replace(`/login?session_expired=1&returnTo=${returnTo}`);
  }
}

/** True when an auth call failed for transient reasons (network, 5xx, timeout) rather than an invalid session.
 * Transient failures must NEVER be treated as "signed out" — that dumps a validly-authenticated user to login. */
export function isTransientAuthError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  if (isInvalidRefreshTokenError(error)) return false;
  const name = (error as { name?: string }).name ?? "";
  if (name === "AuthRetryableFetchError") return true;
  const status = (error as { status?: number }).status;
  if (typeof status === "number" && status >= 500) return true;
  const msg = "message" in error ? String((error as { message: unknown }).message) : "";
  return (
    msg.includes("Failed to fetch") ||
    msg.includes("NetworkError") ||
    msg.includes("Load failed") ||
    msg.includes("timeout") ||
    msg.includes("Timeout")
  );
}

/** Best-effort read of the session supabase-js persisted under the shared LDOS storage key
 * (cookie or localStorage, whichever ldosAuthConfig selected — R-029).
 * Fallback for when getSession() times out on a slow network: trust the stored session instead of
 * logging the user out; onAuthStateChange corrects the cache once the real session settles. */
export function readPersistedSessionFallback(): Session | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = readPersistedAuthRecord();
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const session = parsed?.currentSession ?? parsed;
    if (session?.access_token && session?.user?.id) return session as Session;
    return null;
  } catch {
    return null;
  }
}

export interface CachedAuthUser {
  id: string;
  username?: string;
  email?: string | null;
  createdAt?: string;
}

let cachedAuthUser: CachedAuthUser | null | undefined = undefined;
let cachedSession: Session | null | undefined = undefined;

/** Promise resolved once by AuthContext when initial getSession() completes. All getCurrentSession() waiters share this. */
let resolveInitialAuth: ((s: Session | null) => void) | null = null;
export const initialAuthPromise = new Promise<Session | null>((resolve) => {
  resolveInitialAuth = resolve;
});

function mapAuthUser(user: { id: string; email?: string | null; user_metadata?: any; created_at?: string }): CachedAuthUser {
  return {
    id: user.id,
    username: user.email || user.user_metadata?.username || user.id,
    email: user.email,
    createdAt: user.created_at,
  };
}

/** Called by AuthContext once after getSession() to unblock getCurrentSession() waiters and populate cache. */
export function resolveInitialAuthAndCache(session: Session | null): void {
  if (resolveInitialAuth) {
    resolveInitialAuth(session);
    resolveInitialAuth = null;
  }
  cachedSession = session ?? null;
  cachedAuthUser = session?.user ? mapAuthUser(session.user) : null;
}

export function setCachedAuthUser(user: CachedAuthUser | null) {
  cachedAuthUser = user;
}

export function setCachedSession(session: Session | null) {
  cachedSession = session;
  cachedAuthUser = session?.user ? mapAuthUser(session.user) : null;
}

export function getCachedAuthUser() {
  return cachedAuthUser;
}

async function fetchAuthUser(): Promise<CachedAuthUser | null> {
  if (!isSupabaseConfigured) {
    cachedAuthUser = null;
    return null;
  }
  if (cachedAuthUser !== undefined) {
    return cachedAuthUser;
  }
  const session = await getCurrentSession();
  cachedAuthUser = session?.user ? mapAuthUser(session.user) : null;
  return cachedAuthUser;
}

// Safe helper to get current user - returns null if Supabase not configured
export async function getCurrentUserId(): Promise<string | null> {
  const user = cachedAuthUser !== undefined ? cachedAuthUser : await fetchAuthUser();
  return user?.id || null;
}

// Single source of session: returns cached session or waits for AuthContext's initial getSession().
// Avoids duplicate auth/v1/user and getSession() calls from multiple components.
export async function getCurrentSession(): Promise<Session | null> {
  if (!isSupabaseConfigured) return null;
  if (cachedSession !== undefined) {
    return cachedSession;
  }
  try {
    const session = await initialAuthPromise;
    return session;
  } catch (err) {
    if (isInvalidRefreshTokenError(err)) {
      await redirectToLoginOnSessionExpired();
    }
    return null;
  }
}












